import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Form,
  Hyperlink,
  Icon,
  StatefulButton,
  Tab,
  Tabs,
} from '@openedx/paragon';
import { ChevronLeft } from '@openedx/paragon/icons';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { forgotPassword, setForgotPasswordFormData } from './data/actions';
import { forgotPasswordResultSelector } from './data/selectors';
import ForgotPasswordAlert from './ForgotPasswordAlert';
import messages from './messages';
import BaseContainer from '../base-container';
import { FormGroup } from '../common-components';
import { DEFAULT_STATE, LOGIN_PAGE } from '../data/constants';
import { updatePathWithQueryParams, windowScrollTo } from '../data/utils';
import loginMessage from '../login/messages';
import validateEmail from '../register/RegistrationFields/EmailField/validator';
import validateUsername from '../register/RegistrationFields/UsernameField/validator';

const ForgotPasswordPage = (props) => {
  const {
    SITE_NAME,
    DISABLE_EMAIL_LOGIN,
    PARENT_EMAIL_AS_PRIMARY,
    DISABLE_EMAIL_OR_USERNAME_LOGIN,
    PLACEHOLDER_USERNAME,
    PLACEHOLDER_EMAIL,
    RESET_PASSWORD_BUTTON_TEXT,
    LOGIN_BUTTON_TEXT,
  } = getConfig();
  const {
    backedUpFormData, status, submitState, emailValidationError,
  } = props;

  const { formatMessage } = useIntl();
  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  const [bannerEmail, setBannerEmail] = useState('');
  const [formErrors, setFormErrors] = useState('');
  const [validationError, setValidationError] = useState(emailValidationError);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    sendPageEvent('login_and_registration', 'reset');
    sendTrackEvent('edx.bi.password_reset_form.viewed', { category: 'user-engagement' });
  }, []);

  useEffect(() => {
    setValidationError(emailValidationError);
  }, [emailValidationError]);

  useEffect(() => {
    if (status === 'complete') {
      setFormFields(prevFields => ({
        ...prevFields,
        username: '',
        email: '',
        emailOrUsername: '',
      }));
    }
  }, [status]);

  const validateEmailOrUsername = (value) => {
    if (!value) {
      return formatMessage(messages['forgot.password.empty.emailorUsername.field.error']);
    }
    if (value.length < 2) {
      return formatMessage(messages['username.or.email.format.validation.less.chars.message']);
    }
    return null;
  };

  const isEmailLogin = DISABLE_EMAIL_OR_USERNAME_LOGIN && !DISABLE_EMAIL_LOGIN && !PARENT_EMAIL_AS_PRIMARY;
  const isUsernameLogin = (DISABLE_EMAIL_OR_USERNAME_LOGIN && DISABLE_EMAIL_LOGIN) || PARENT_EMAIL_AS_PRIMARY;
  const isEmailOrUsernameLogin = !DISABLE_EMAIL_OR_USERNAME_LOGIN && !PARENT_EMAIL_AS_PRIMARY;
  const placeholderUsername = PLACEHOLDER_USERNAME || 'Username';
  const placeholderEmail = PLACEHOLDER_EMAIL || 'email';
  const resetPasswordButtonText = RESET_PASSWORD_BUTTON_TEXT || 'Submit';
  const loginButtonText = LOGIN_BUTTON_TEXT || 'Sign in';

  const getValidationMessage = (value) => {
    const isValueObject = typeof value === 'object';
    const email = isValueObject ? value.email : value;
    const username = isValueObject ? value.username : value;
    const emailOrUsername = isValueObject ? value.emailOrUsername : value;

    if (isEmailOrUsernameLogin) {
      const error = validateEmailOrUsername(emailOrUsername);
      if (error) { return error; }
    }

    if (isUsernameLogin) {
      const error = validateUsername(username, formatMessage);
      if (error) { return error; }
    }

    if (isEmailLogin) {
      const error = validateEmail(email, '', formatMessage);
      if (error) { return error.fieldError; }
    }

    return '';
  };
  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setFormFields(prevState => ({ ...prevState, [name]: value }));
    props.setForgotPasswordFormData({ emailValidationError: '' });
  };

  const handleBlur = () => {
    if (!hasSubmitted) {
      return;
    }
    const formData = { ...formFields };
    let errorFieldName;
    if (isEmailOrUsernameLogin) {
      errorFieldName = formData.emailOrUsername;
    } else if (isUsernameLogin) {
      errorFieldName = formData.username;
    } else {
      errorFieldName = formData.email;
    }
    props.setForgotPasswordFormData({ errorFieldName, emailValidationError: getValidationMessage(errorFieldName) });
  };

  const handleFocus = () => props.setForgotPasswordFormData({ emailValidationError: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    setHasSubmitted(true);
    const formData = { ...formFields };
    const error = getValidationMessage(formData);
    let errorFieldName;
    if (isEmailOrUsernameLogin) {
      errorFieldName = formData.emailOrUsername;
    } else if (isUsernameLogin) {
      errorFieldName = formData.username;
    } else {
      errorFieldName = formData.email;
    }
    setBannerEmail(errorFieldName);
    if (error) {
      setFormErrors(error);
      props.setForgotPasswordFormData({ errorFieldName, emailValidationError: error });
      windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
    } else {
      setFormErrors(error);
      props.forgotPassword(errorFieldName);
    }
  };

  const tabTitle = (
    <div className="d-inline-flex flex-wrap align-items-center">
      <Icon src={ChevronLeft} />
      <span className="ml-2">{formatMessage(messages['sign.in.text'], { label: loginButtonText })}</span>
    </div>
  );

  let instructionText;
  let fieldLabel;
  let fieldName;
  let fieldValue;
  let fieldHelpText;

  if (isEmailOrUsernameLogin) {
    fieldName = 'emailOrUsername';
    fieldValue = formFields.emailOrUsername;
    fieldLabel = formatMessage(loginMessage['login.user.identity.label'], { label: `${placeholderUsername} or ${placeholderEmail}` });
    fieldHelpText = [formatMessage(messages['forgot.password.emailorUsername.help.text'], { SITE_NAME })];
    instructionText = formatMessage(messages['forgot.password.page.instructions']);
  } else if (isUsernameLogin) {
    fieldName = 'username';
    fieldValue = formFields.username;
    fieldLabel = formatMessage(loginMessage['login.username.identity.label'], { label: placeholderUsername });
    fieldHelpText = [formatMessage(messages['forgot.password.username.help.text'], { SITE_NAME })];
    instructionText = formatMessage(messages['forgot.password.username.page.instructions']);
  } else {
    fieldName = 'email';
    fieldValue = formFields.email;
    fieldLabel = formatMessage(loginMessage['login.email.identity.label'], { label: placeholderEmail });
    fieldHelpText = [formatMessage(messages['forgot.password.email.help.text'], { SITE_NAME })];
    instructionText = formatMessage(messages['forgot.password.email.page.instructions']);
  }

  return (
    <BaseContainer>
      <Helmet>
        <title>{formatMessage(messages['forgot.password.page.title'],
          { siteName: getConfig().SITE_NAME })}
        </title>
      </Helmet>
      <div>
        <Tabs activeKey="" id="controlled-tab" onSelect={(key) => navigate(updatePathWithQueryParams(key))}>
          <Tab title={tabTitle} eventKey={LOGIN_PAGE} />
        </Tabs>
        <div id="main-content" className="main-content">
          <Form id="forget-password-form" name="forget-password-form" className="mw-xs">
            <ForgotPasswordAlert email={bannerEmail} emailError={formErrors} status={status} />
            <h2 className="h4">
              {formatMessage(messages['forgot.password.page.heading'])}
            </h2>
            <p className="mb-4">
              {instructionText}
            </p>
            <FormGroup
              floatingLabel={fieldLabel}
              name={fieldName}
              value={fieldValue}
              autoComplete="on"
              errorMessage={validationError}
              handleChange={handleOnChange}
              handleBlur={handleBlur}
              handleFocus={handleFocus}
              helpText={fieldHelpText}
            />
            <StatefulButton
              id="submit-forget-password"
              name="submit-forget-password"
              type="submit"
              variant="brand"
              className="forgot-password--button"
              state={submitState}
              labels={{
                default: formatMessage(messages['forgot.password.page.submit.button'], { label: resetPasswordButtonText }),
                pending: '',
              }}
              onClick={handleSubmit}
              onMouseDown={(e) => e.preventDefault()}
            />
            {(getConfig().LOGIN_ISSUE_SUPPORT_LINK) && (
              <Hyperlink
                id="forgot-password"
                name="forgot-password"
                className="ml-4 font-weight-500 text-body"
                destination={getConfig().LOGIN_ISSUE_SUPPORT_LINK}
                target="_blank"
                showLaunchIcon={false}
              >
                {formatMessage(messages['need.help.sign.in.text'])}
              </Hyperlink>
            )}
            <p className="mt-5.5 small text-gray-700">
              {formatMessage(messages['additional.help.text'], { SITE_NAME })}
              <span>
                <Hyperlink isInline destination={`mailto:${getConfig().INFO_EMAIL}`}>{getConfig().INFO_EMAIL}</Hyperlink>
              </span>
            </p>
          </Form>
        </div>
      </div>
    </BaseContainer>
  );
};

ForgotPasswordPage.propTypes = {
  backedUpFormData: PropTypes.shape({
    formFields: PropTypes.shape({}),
    errors: PropTypes.shape({}),
  }),
  emailValidationError: PropTypes.string,
  forgotPassword: PropTypes.func.isRequired,
  setForgotPasswordFormData: PropTypes.func.isRequired,
  status: PropTypes.string,
  submitState: PropTypes.string,
};

ForgotPasswordPage.defaultProps = {
  backedUpFormData: {
    formFields: {
      username: '', email: '', emailOrUsername: '', password: '',
    },
    errors: {
      username: '', email: '', emailOrUsername: '', password: '',
    },
  },
  emailValidationError: '',
  status: null,
  submitState: DEFAULT_STATE,
};

export default connect(
  forgotPasswordResultSelector,
  {
    forgotPassword,
    setForgotPasswordFormData,
  },
)(ForgotPasswordPage);
